import type { Guide, GuideRecordHint } from '../types';

/**
 * S-29.10-01 — podgląd źródła przy prezleceniu (status prac 28.10.2026 pkt 5; scenariusz e2e 42). S42 —
 * prezlecenie z maila DEMO z zamówieniem w PDF, listą w arkuszu i plikiem od klienta (`e2e/setup/seed-e2e.ts`,
 * `scenario-state.mjs`). Nagrywarka tylko otwiera źródło i przełącza zakładki, niczego nie zapisuje.
 */
const FROM_MAIL: GuideRecordHint = {
  description: 'prezlecenie z maila z załącznikami — z listy prezleceń',
  listPath: '/pre-orders',
};

export const guide: Guide = {
  flagKey: 'sourcePreview',
  title: 'Podgląd źródła przy prezleceniu',
  whatItDoes:
    'Gdy w prezleceniu brakuje danej albo nie dało się jej odczytać, sprawdzisz ją w tym, co przysłał klient — bez szukania maila w poczcie. Przycisk „Pokaż źródło” na karcie prezlecenia i w oknie kontroli otwiera obok danych podgląd: zakładka „Mail” z treścią wiadomości i po jednej zakładce na każdy załącznik i plik od klienta. PDF i zdjęcie otwierają się na miejscu, arkusz i dokument pobierasz. Przy polu bez wartości albo odczytanym niepewnie „pokaż w źródle” otwiera właściwy załącznik. Źródło jest tylko do odczytu; planiści FTL i Trade oglądają je tak samo, bez zmian w danych.',
  audience: ['CS', 'Administracja', 'planista FTL', 'Team Leader (planista główny)', 'Trade'],
  presentationItems: ['S-29.10-01'],
  steps: [
    {
      route: '/pre-orders/:preOrderId/show',
      record: FROM_MAIL,
      target: 'source-preview-toggle',
      title: '„Pokaż źródło” — mail i załączniki pod przyciskiem',
      description:
        'Źródło nie zajmuje ekranu, dopóki go nie potrzebujesz. Przycisk podaje, ile jest materiałów: mail i jego załączniki oraz pliki od klienta. Raz otwarte źródło zostaje otwarte przy kolejnych prezleceniach, dopóki go nie ukryjesz.',
      action: 'Kliknij „Pokaż źródło”.',
      recorder: { account: 'cs', demo: { kind: 'scenario', key: 's42PreOrderId' } },
    },
    {
      route: '/pre-orders/:preOrderId/show',
      record: FROM_MAIL,
      target: 'source-preview-panel',
      title: 'Dane po jednej stronie, źródło po drugiej',
      description:
        'Zakładka „Mail” pokazuje nadawcę, adresatów, datę i treść wiadomości. Treść jest pokazana bezpiecznie: bez skryptów, formularzy i obrazów z internetu. Kolejne zakładki to załączniki maila i pliki od klienta.',
      action: 'Porównaj dane prezlecenia z treścią maila.',
      recorder: {
        account: 'cs',
        demo: { kind: 'scenario', key: 's42PreOrderId' },
        // „pokaż w źródle” otwiera źródło niezależnie od zapamiętanego stanu przycisku (przełącza, nie otwiera).
        actions: [
          { kind: 'click', target: 'source-preview-show-in-source' },
          { kind: 'clickText', text: 'Mail' },
        ],
      },
    },
    {
      route: '/pre-orders/:preOrderId/show',
      record: FROM_MAIL,
      target: 'source-preview-pdf',
      title: 'Zamówienie w PDF na miejscu, arkusz do pobrania',
      description:
        'PDF i zdjęcie otwierają się w zakładce. Arkusza i dokumentu aplikacja jeszcze nie wyświetla — zakładka mówi to wprost i ma przycisk „Pobierz oryginał”.',
      action: 'Kliknij zakładkę z plikiem PDF.',
      recorder: {
        account: 'cs',
        demo: { kind: 'scenario', key: 's42PreOrderId' },
        // Pierwszy „pokaż w źródle” stoi przy załadunku bez godziny i otwiera zamówienie w PDF.
        actions: [{ kind: 'click', target: 'source-preview-show-in-source' }],
      },
    },
    {
      route: '/pre-orders/:preOrderId/show',
      record: FROM_MAIL,
      target: 'source-preview-show-in-source',
      title: '„pokaż w źródle” przy brakującej albo niepewnej danej',
      description:
        'Przy polu bez wartości (na przykład godziny załadunku, której nie było w treści maila) albo odczytanym z niską pewnością stoi „pokaż w źródle”. Odnośnik otwiera źródło na załączniku, z którego pole pochodzi — wartość wpisujesz w danych prezlecenia, nie w źródle.',
      action: 'Kliknij „pokaż w źródle” przy załadunku.',
      recorder: { account: 'cs', demo: { kind: 'scenario', key: 's42PreOrderId' } },
    },
    {
      route: '/pre-orders/:preOrderId/control',
      record: FROM_MAIL,
      target: 'source-preview-toggle',
      title: 'To samo źródło w oknie kontroli',
      description:
        'Administracja otwiera źródło w oknie kontroli, obok pól blokujących zapis i decyzji — sprawdza dane z tym, co przysłał klient, bez przechodzenia na kartę.',
      action: 'W oknie kontroli kliknij „Pokaż źródło”.',
      recorder: { account: 'adm', demo: { kind: 'scenario', key: 's42PreOrderId' } },
    },
  ],
};
